import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Country } from '../models/country';
import { HttpClient } from '@angular/common/http';
import { Zone } from '../models/zone';
import { Package, PackageArea, PackageAreaCreate, PackageExtended, PackagePatch, PackageZone, PackageZoneCreate } from '../models/package';
import { Area } from '../models/area';
import { UsageRate, UsageRateArea, UsageRateAreaCreate, UsageRateAreaPatch, UsageRateExtended, UsageRatePatch, UsageRateZone, UsageRateZonePatch } from '../models/usage-rate';
import { Service } from '../models/service';

@Injectable({
  providedIn: 'root',
})
export class BackendApi {
  constructor(private readonly http: HttpClient) {}

  createUsageRate(newUsageRate: UsageRate): Observable<UsageRate> {
    return this.http.post<UsageRate>(`${environment.backendBaseUrl}/usageRates`, newUsageRate);
  }

  getUsageRates(): Observable<UsageRate[]> {
    return this.http.get<UsageRate[]>(`${environment.backendBaseUrl}/usageRates`);
  }

  getUsageRate(usageRateUuid: string): Observable<UsageRateExtended> {
    return this.http.get<UsageRateExtended>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}`);
  }

  patchUsageRate(usageRateUuid: string, usageRate: UsageRatePatch): Observable<UsageRate> {
    return this.http.patch<UsageRate>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}`, usageRate);
  }

  deleteUsageRate(usageRateUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}`);
  }

  createUsageRateArea(usageRateUuid: string, usageRateArea: UsageRateAreaCreate): Observable<UsageRateArea> {
    return this.http.post<UsageRateArea>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}/areas`, usageRateArea);
  }

  patchUsageRateArea(usageRateUuid: string, usageRateAreaUuid: string, usageRateArea: UsageRateAreaPatch): Observable<UsageRateArea> {
    return this.http.patch<UsageRateArea>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}/areas/${usageRateAreaUuid}`, usageRateArea);
  }

  deleteUsageRateArea(usageRateUuid: string, usageRateAreaUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}/areas/${usageRateAreaUuid}`);
  }

  createUsageRateZone(usageRateUuid: string, usageRateZone: UsageRateZone): Observable<UsageRateZone> {
    return this.http.post<UsageRateZone>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}/zones`, usageRateZone);
  }

  patchUsageRateZone(usageRateUuid: string, usageRateZoneUuid: string, usageRateZone: UsageRateZonePatch): Observable<UsageRateZone> {
    return this.http.patch<UsageRateZone>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}/zones/${usageRateZoneUuid}`, usageRateZone);
  }

  deleteUsageRateZone(usageRateUuid: string, usageRateZoneUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/usageRates/${usageRateUuid}/zones/${usageRateZoneUuid}`);
  }

  getAreas(): Observable<Area[]> {
    return this.http.get<Area[]>(`${environment.backendBaseUrl}/areas`);
  }

  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${environment.backendBaseUrl}/countries`);
  }

  getZones(): Observable<Zone[]> {
    return this.http.get<Zone[]>(`${environment.backendBaseUrl}/zones`);
  }

  getPackages(): Observable<Package[]> {
    return this.http.get<Package[]>(`${environment.backendBaseUrl}/packages`);
  }

  getPackage(packageUuid: string): Observable<PackageExtended> {
    return this.http.get<PackageExtended>(`${environment.backendBaseUrl}/packages/${packageUuid}`);
  }

  createPackage(newPackage: Package): Observable<Package> {
    return this.http.post<Package>(`${environment.backendBaseUrl}/packages`, newPackage);
  }

  patchPackage(packageUuid: string, updatedPackage: PackagePatch): Observable<Package> {
    return this.http.patch<Package>(`${environment.backendBaseUrl}/packages/${packageUuid}`, updatedPackage);
  }

  deletePackage(packageUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/packages/${packageUuid}`);
  }

  createPackageArea(packageUuid: string, packageArea: PackageAreaCreate): Observable<PackageArea> {
    return this.http.post<PackageArea>(`${environment.backendBaseUrl}/packages/${packageUuid}/areas`, packageArea);
  }

  deletePackageArea(packageUuid: string, packageAreaUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/packages/${packageUuid}/areas/${packageAreaUuid}`);
  }

  createPackageZone(packageUuid: string, packageZone: PackageZoneCreate): Observable<PackageZone> {
    return this.http.post<PackageZone>(`${environment.backendBaseUrl}/packages/${packageUuid}/zones`, packageZone);
  }

  deletePackageZone(packageUuid: string, packageZoneUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/packages/${packageUuid}/zones/${packageZoneUuid}`);
  }

  getServices(): Observable<Service[]> {
    return this.http.get<Service[]>(`${environment.backendBaseUrl}/services`);
  }
}
